import {
  CONVEYANCE_TYPES,
  Customizer,
  type MenuProductOption,
  type MenuProductOptionGroup,
  type MenuProductWithCategory,
  type BasketItem,
  type BasketItemOption,
  type BasketOrder,
  type Category,
  type DeliveryAddress,
  type MenuProduct,
  sdkUtils,
} from '@koala/sdk';
import { encode } from 'html-entities';
import each from 'lodash/each';
import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import sum from 'lodash/fp/sum';
import remove from 'lodash/remove';
import { getPersistentParameterValue } from './global';
import { type API_CONVEYANCE_TYPES } from '@/constants/events';
import {
  ALLOWED_URL_PARAMETER_KEYS,
  API_FOR_CONVEYANCE_TYPES_SWAP,
  LOCKED_FLOW_CONVEYANCE_TYPES,
} from '@/constants/global';
import { type BasketItemFinal } from '@/types/checkout';
import { type IGlobalToastReopen } from '@/types/global';
import { sentryLogger } from '@/utils/sentryLogger';

/**
 * Get subtotal for an array of cart items
 *
 * @param {Array} products - assumes format matches the nullItem
 */
export const getCartSubtotal = (basketItems: (BasketItemFinal | null)[]): number => {
  // does not currently account for the cost of selected modifiers --> SDK!
  return (
    flow([
      map((basketItem: BasketItemFinal | null) => {
        // If we have a null item, the product id in our cart no longer exists in the menu
        // This condition will return a price of 0 for the null item so it at least doesn't crash the cart
        // However, the invalid item will still exist in sessionStorage
        // invalid items are removed from sessionStorage when INIT_SESSION_STORAGE_BASKET is fired
        if (!basketItem) {
          return 0;
        }

        if (basketItem.final.final_cost) {
          return basketItem.final.final_cost * basketItem.item.quantity;
        }

        return basketItem.final.cost;
      }),
      sum,
    ]) as unknown as (basketItems: (BasketItemFinal | null)[]) => number
  )(basketItems);
};

export const toDollars = (x: number | undefined, quantity = 1) => {
  if (typeof x === 'number') {
    return ((x * quantity) / 100).toFixed(2);
  }
  return '';
};

export const invertNumberIfOptionInverted = (value: number, isInverted: boolean) => {
  if (!isInverted || value === 0) {
    return value;
  }
  return value > 0 ? value * -1 : Math.abs(value);
};

/**
 * Find a MenuProduct by global_id given a menu
 *
 * @param {itemGlobalId} string
 * @param {menuCategories} array
 */
const findMenuItemByGlobalId = (itemGlobalId: string, menuCategories: Category[]) => {
  let product: MenuProduct | undefined;

  each(menuCategories, (category: Category) => {
    each(category.products, (menuProduct: MenuProduct) => {
      if (menuProduct.global_id === itemGlobalId) {
        product = menuProduct;
        return false;
      }

      return;
    });

    if (product && product.id) {
      return false;
    }

    return;
  });

  return product;
};

type Option = BasketItem['options'][number];

export const getOptionsDictionary = (selectedOptions: BasketItem['options'] | string[]) => {
  const newOptionsDictionary:
    | Record<Option['id'], Option>
    | Record<string, { id: string; quantity: number }> = {};

  selectedOptions.forEach((option) =>
    typeof option === 'object'
      ? (newOptionsDictionary[option.option_id ?? option.id] = option)
      : (newOptionsDictionary[option] = { id: option, quantity: 1 }),
  );

  return newOptionsDictionary;
};

/**
 * @param  {BasketItem["options"]} basketItemOptions - collections of the basket item options selected by user
 * @param  {MenuProductOptionGroup[]} currentLocationProductOptionGroups - collection of option groups of the product that was fetch by product id (per location)
 * @param  {MenuProductOptionGroup[]} newLocationProductOptionGroups - collection of option groups of the product that was fetch by global product id (per brand)
 */
const migrateOptions = (
  basketItemOptions: BasketItem['options'],
  currentLocationProductOptionGroups: MenuProductOptionGroup[],
  newLocationProductOptionGroups: MenuProductOptionGroup[],
) => {
  // Create a dictionary of IDs to better keep track of quantities
  const newOptionsDictionary = getOptionsDictionary(basketItemOptions);

  const selectedOptionIdsOnly = Object.keys(newOptionsDictionary);
  const globalOptions: MenuProductOption[] = [];
  const migratedOptions: MenuProductOption[] = [];

  // Recursive function to search through all the product options until all of the selected options are found.
  /** @TODO improve typing of `currentOptionGroups`. */
  const loopThroughOptionGroups = (
    currentLocationProductOptionGroups: MenuProductOptionGroup[],
  ) => {
    // Loop through the option groups
    each(currentLocationProductOptionGroups, (og) => {
      // Loop through the option group options
      each(og.options, (productOption) => {
        // Base Case for recursion
        // Stop if we've eliminated all the selected options
        if (selectedOptionIdsOnly.length === 0) {
          return false;
        }

        if (selectedOptionIdsOnly.includes(productOption.id)) {
          const containsSubOptions = !!productOption.option_groups.length;

          globalOptions.push({
            ...productOption,
            quantity: newOptionsDictionary[productOption.id].quantity,
          });

          // Remove the found option
          remove(
            selectedOptionIdsOnly,
            (selectedOptionId) => selectedOptionId === productOption.id,
          );

          // If the selected option has sub options, search those
          if (containsSubOptions) {
            loopThroughOptionGroups(productOption.option_groups);
          }
        }
      });

      // Stop if we've eliminated all the selected options
      if (selectedOptionIdsOnly.length === 0) {
        return false;
      }
    });
  };

  // Recursive function to search through all the product options until all of the selected options are found.
  const loopThroughNewOptionGroups = (newLocationProductOptionGroups: MenuProductOptionGroup[]) => {
    // Loop through the option groups
    each(newLocationProductOptionGroups, (og) => {
      if (og)
        // Loop through the option group options
        each(og.options, (globalProductOption) => {
          // Stop if we've eliminated all the selected options
          if (globalOptions.length === 0) {
            return false;
          }

          const foundGlobalOption = globalOptions.find(
            (opt) => opt.global_option_id === globalProductOption.global_option_id,
          );

          if (foundGlobalOption) {
            const containsSubOptions = !!globalProductOption.option_groups.length;

            migratedOptions.push({
              ...globalProductOption,
              quantity: foundGlobalOption.quantity,
            });

            // Remove the found option
            remove(
              globalOptions,
              (so) => so.global_option_id === globalProductOption.global_option_id,
            );
            // If the selected option has sub options, search those
            if (containsSubOptions) {
              loopThroughNewOptionGroups(globalProductOption.option_groups);
            }
          }
        });
      // Stop if we've eliminated all the selected options
      if (globalOptions.length === 0) {
        return false;
      }
    });
  };

  // Start the search for global options
  loopThroughOptionGroups(currentLocationProductOptionGroups);
  // Match the global options with the new options group.
  loopThroughNewOptionGroups(newLocationProductOptionGroups);

  return migratedOptions;
};

/**
 * Find a Option by option id given a option group
 *
 * @param optionId
 * @param optionGroups
 * @TODO improve typing of optionGroups.
 */
export const findOptionByOptionId = (optionId: string, optionGroups: MenuProductOptionGroup[]) => {
  let optionGroupOption: MenuProductOption | undefined;

  //Search through option groups for the option
  for (const og of optionGroups) {
    if (optionGroupOption) {
      break;
    }

    optionGroupOption = og.options.find((o) => o.id === optionId);
  }

  return optionGroupOption;
};

export const migrateBasketItems = (
  basketItems: BasketItem[],
  menuCategories: Category[],
  newMenuCategories: Category[],
) => {
  const productsNotMatched: MenuProductWithCategory[] = [];
  const migratedBasketItems = basketItems
    .map((item) => {
      const migratedItem = { ...item };

      const currentLocationProduct = sdkUtils.findMenuItemById(
        migratedItem.product.id,
        menuCategories,
      );

      // Logic:
      // 1. we get current location product from menu by product id that user put in basket
      // 2. if that product found -> we try to find same product by global id for future location
      // 3. once that product found in new location menu we try to migrate options and apply them to existing basket item
      // 4. else we push current location product to not matched products collection
      if (currentLocationProduct) {
        const newLocationProduct = findMenuItemByGlobalId(
          currentLocationProduct.global_id,
          newMenuCategories,
        );

        if (newLocationProduct) {
          const migratedOptions = migrateOptions(
            item.options,
            currentLocationProduct.option_groups,
            newLocationProduct.option_groups,
          );

          migratedItem.product.id = newLocationProduct.id;

          // TODO: remove that mapping when we sync Basket Item Option model with Product Option Model
          migratedItem.options = migratedOptions.map((productOption) => ({
            ...productOption,
            quantity: productOption.quantity ?? 0,
          }));

          return migratedItem;
        }
      }

      // Not Found
      if (currentLocationProduct) {
        productsNotMatched.push(currentLocationProduct);
      }
    })
    .filter((item): item is BasketItem => item !== undefined);

  return {
    migratedBasketItems,
    productsNotMatched,
  };
};

export function patchProductOptions(basketItem: BasketItem) {
  return {
    ...basketItem,
    options: basketItem.options.map((option) => ({
      ...option,
      option_id: option.option_id ?? option.id,
    })),
  };
}

export const patchBasketProductOptions = (basketOrder: BasketOrder) => ({
  ...basketOrder,
  basket_items: basketOrder.basket_items.map(patchProductOptions),
});

export const basketCheckInvalidItems = (basketItems: BasketItem[], menuCategories: Category[]) => {
  return basketItems.filter((item) => !sdkUtils.findMenuItemById(item.product.id, menuCategories));
};

// Method to remove cart items that can not be found in the menu categories.
// whoever calls this method should use it to update in localStorage
export const removeInvalidItemsFromCart = (
  basketItems: BasketItem[],
  menuCategories: Category[],
) => {
  const validItems: BasketItem[] = [];

  basketItems.forEach((item) => {
    const menuProduct = sdkUtils.findMenuItemById(item.product.id, menuCategories);
    if (menuProduct) {
      validItems.push(item);
    }
  });

  return validItems;
};

/*
 * Recreates a Customizer product with existingOptions passed in (for editing a product)
 */
export const recreateCustomizerProductFromMenuProduct = (
  product: MenuProduct,
  options: BasketItemOption[],
): MenuProduct => {
  // Recreate existingOptions object
  const existingOptions = prepareExistingOptionsObject(options);

  // Create new Customizer product with existingOptions
  const customize = new Customizer(product, existingOptions);
  return customize.getProduct();
};

/** One cart line that cannot satisfy a required option group, and the groups it is missing. */
export interface UnsatisfiedBasketLine {
  /** Position in the array the item will live at in `basket.content.basket_items`. */
  index: number;
  groups: MenuProductOptionGroup[];
}

/**
 * The toast the diner sees when a cart line can no longer satisfy a required option — TEAM-15138.
 *
 * Shared by `revalidateBasketItemsSaga` and both reorder paths so one wording and one affordance
 * cover every way a stale cart reaches the diner. Returns `null` when there is nothing to say,
 * which keeps the "should we toast at all?" decision at the call site.
 *
 * The sentence deliberately does **not** duplicate coo-api's `required_option_missing`
 * `user_message`. This fires *before* any request is made, so there is no envelope to defer to; the
 * server-side rejection keeps its own copy at tier 3 of the render rule. Both name the same
 * situation because it is the same situation.
 *
 * ## The product name is operator-authored, and `message` is an innerHTML sink
 *
 * `components/toast/index.tsx` renders `message` with `dangerouslySetInnerHTML`, and the sentence
 * interpolates `product.name` — merchant-authored menu content this repo does not control. So the
 * name is `encode`d on the way in, the same treatment `getVettedUpstreamCopy` (`@/utils/fpoErrors`)
 * gives coo-api's `user_message` for the same sink.
 *
 * This is escaping, not sanitising: a product legitimately called `Ben & Jerry's` renders
 * identically either way, because innerHTML decodes `&amp;` back. What changes is a name containing
 * `<`, which would otherwise be parsed as markup.
 *
 * `ctaLabel` needs no such treatment — the component renders it as a plain React child, which
 * escapes by construction. The two are asserted separately in
 * `utils/__tests__/basket.ts` and `components/toast/toast.test.tsx`.
 *
 * The rest of the copy constraints mirror `@/constants/fpoErrorCopy`: the fixed words carry no `<`
 * or `&` of their own, and never the substrings coo-api's
 * `Handler::isRestaurantTemporarilyUnavailable` matches on.
 */
export const buildUnsatisfiedOptionsPrompt = (
  lines: UnsatisfiedBasketLine[],
  items: BasketItem[],
): { message: string; reopen: IGlobalToastReopen } | null => {
  const [first] = lines;
  if (!first) {
    return null;
  }

  const item = items[first.index];
  const itemName = item?.product.name ?? 'An item in your cart';
  const groupName = first.groups[0]?.description;

  return {
    message:
      lines.length === 1
        ? `${encode(itemName)} needs a required choice that is no longer part of your selection.`
        : `${lines.length} items in your cart need a required choice that is no longer part of your selection.`,
    reopen: {
      basketItemIndex: first.index,
      expectedSignature: item ? basketItemOptionSignature(item) : undefined,
      ctaLabel: groupName ? `Choose your ${groupName}` : `Update ${itemName}`,
    },
  };
};

/**
 * Every cart line in `items` that can no longer satisfy a required option group — TEAM-15138.
 *
 * Indices are positions in the array passed in, so callers must pass the array the diner will
 * actually end up with. That matters on the reorder paths, where `filterAvailableItems` drops
 * lines: positions in the past order do not survive into the new cart.
 */
export const findUnsatisfiedBasketLines = (
  items: BasketItem[],
  menuCategories: Category[],
): UnsatisfiedBasketLine[] =>
  items
    .map((item, index) => ({
      index,
      groups: findUnsatisfiedOptionGroupsForBasketItem(item, menuCategories),
    }))
    .filter(({ groups }) => groups.length > 0);

/**
 * A stable identity for "this cart line, with these options" — TEAM-15138.
 *
 * Not the array index: that shifts whenever `removeInvalidItemsFromCart` drops a line, which would
 * silence a new problem and re-fire an old one. Reads `option_id ?? id` for the same reason
 * `prepareExistingOptionsObject` does — the two carry the menu option id on different sides of a
 * server round-trip. Sorted so option order never changes the identity.
 *
 * **Not unique per line** — two identical cart lines share one. `unsatisfiedOptionsPromptKey` is
 * what the prompt memory records; see there for why that is not a problem to be solved per line.
 */
export const basketItemOptionSignature = (item: BasketItem): string => {
  const optionIds = (item.options ?? [])
    .map((option: BasketItemOption | string) =>
      typeof option === 'string' ? option : option?.option_id ?? option?.id,
    )
    .filter(Boolean)
    .sort();
  return `${item.product.id}|${optionIds.join(',')}`;
};

/**
 * The identity of one unsatisfied-options prompt — TEAM-15138.
 *
 * `sessionStorageOptionPrompts` stores this so the prompt does not repeat on every route change,
 * and the shape of the key is the whole design. It digests **every** unsatisfied line's signature,
 * in cart order, rather than keying each line separately.
 *
 * Per-line keys look obviously right and are wrong in the case that matters most. Two identical
 * cart lines — the same product with the same options, which is ordinary, and which one menu change
 * breaks both of at once — share a signature. Disambiguating them by position then fails the moment
 * the diner fixes one: the survivor slides into the other's slot, inherits a key already recorded,
 * and is silenced permanently. That line reaches checkout with no prompt and no way back, which is
 * the exact failure this work exists to close.
 *
 * A whole-set digest changes when the set changes — a line fixed, another broken, the menu moving
 * again — and does not change when the diner simply ignores the toast. That is the behaviour
 * wanted, and it needs no per-line identity at all.
 *
 * Returns `null` when nothing is unsatisfied, which callers store to clear the memory.
 *
 * All three call sites (`revalidateBasketItemsSaga` and both reorder paths) build the key here so
 * what they write and what they read can never drift.
 */
export const unsatisfiedOptionsPromptKey = (
  lines: UnsatisfiedBasketLine[],
  items: BasketItem[],
): string | null =>
  lines.length
    ? lines
        .map(({ index }) => {
          const item = items[index];
          return item ? basketItemOptionSignature(item) : `?${index}`;
        })
        .join(';')
    : null;

/**
 * The required option groups a **persisted** cart item can no longer satisfy — TEAM-15138.
 *
 * `revalidateBasketItemsSaga` and both reorder paths previously checked only base price drift and
 * whether the product still existed, so an item whose option groups changed underneath it sailed
 * through to checkout and was rejected there by coo-api with `required_option_missing`. This is the
 * missing check.
 *
 * Persisted options are a **flat** `[{option_id, quantity}]` list carrying no group information at
 * any nesting depth (`basketItemOption` in the SDK), which is the same reason coo-api has to
 * re-derive grouping from its own menu. Recovering the structure means re-walking the freshly
 * fetched product, which is exactly what `recreateCustomizerProductFromMenuProduct` above already
 * does for display and pricing — reused here rather than reimplemented, so the validity flags this
 * reads are produced by the same `Customizer` pass the customizer modal runs.
 *
 * Returns `[]` when the product is gone from the menu: that case belongs to
 * `removeInvalidItemsFromCart`, and reporting it here would ask the diner to reopen an item that no
 * longer exists.
 *
 * ## Never throws
 *
 * `redux/store.ts` registers no saga `onError`, so an uncaught throw inside a `takeLatest` worker
 * aborts the fork tree up to the root `all([...])` and silently kills **every** saga in the app —
 * the defect TEAM-15059 found and fixed on the add-item path. This runs over sessionStorage
 * payloads up to two hours old whose option shape may predate current code (see
 * `prepareExistingOptionsObject` below for the three shapes that have existed), and `Customizer` is
 * not defensive about what it is handed. A malformed item is therefore reported as satisfied and
 * logged, never propagated: a missed prompt is a bad outcome, a dead app is a worse one.
 */
export const findUnsatisfiedOptionGroupsForBasketItem = (
  item: BasketItem,
  menuCategories: Category[],
): MenuProductOptionGroup[] => {
  try {
    const menuProduct = sdkUtils.findMenuItemById(item.product.id, menuCategories);
    if (!menuProduct) {
      return [];
    }
    const final = recreateCustomizerProductFromMenuProduct(menuProduct, item.options);
    return sdkUtils.getUnsatisfiedOptionGroups(final);
  } catch (error) {
    sentryLogger.warn('Cart revalidation: could not re-check option groups for a cart item', {
      eventName: 'CART_OPTION_REVALIDATION_FAILED',
      productId: item.product.id,
      error,
    });
    return [];
  }
};

/*
 * Creates `existingOptions` object to pass to Customizer
 *
 * It seems that this function create object with basket item option id and its quantity.
 * The problem function is trying to solve is that at some point options are passed whether as:
 * - array of string
 * - BasketItemOption without option_id and id matching id of menu option
 * - BasketItemOption with option_id that matches id of menu option and id that is unique and does not match id of menu option
 *
 * TODO: This function needs to be refactored.
 * We need to narrow down why and where Basket Item options are populated and set.
 * Why {options} can be passed as array of strings or array of BasketItemOption.
 * We need to make it consistent and end up using one model BasketItemOption where potentially option_id AND id
 * are optional parameters that are set on creating Basket Item option on basket.
 */
export const prepareExistingOptionsObject = (options: BasketItemOption[] | string[]) => {
  const existingOptions: Record<string, { id: string; quantity: number }> = {};

  options.map((option: BasketItemOption | string) => {
    // TODO: Remove casting of option to any when option_id is added to BasketItemOption
    const optionId =
      typeof option === 'string' ? option : option?.option_id ? option?.option_id : option.id;
    const optionQuantity = typeof option === 'string' ? 1 : option.quantity;

    // Create dictionary of existing options and quantities
    existingOptions[optionId] = {
      id: optionId,
      quantity: optionQuantity,
    };
  });

  return existingOptions;
};

/* basketProducts is an array of all the selected products trimmed down to their options
 similar to the customization page
 just need to iterate over every branch of the product options to render
 */
export const basketProductsWithOptionPricing = (
  basketContent: BasketOrder,
  menuCategories: Category[],
) =>
  basketContent.basket_items.map((item) => {
    const menuProduct = sdkUtils.findMenuItemById(item.product.id, menuCategories);
    if (menuProduct) {
      const final = recreateCustomizerProductFromMenuProduct(menuProduct, item.options);
      return {
        item,
        final,
        menuProduct,
      };
    }
    return null;
  });

/**
 * Determines a basket's fulfillment type based on the priorities, in order of importance:
 *
 * 1. Whether or not the user is in a locked conveyance flow (should always take precedence)
 * 2. Whether or not an address is present
 *
 * @param address deliveryAddress
 * @returns CONVEYANCE_TYPES
 */
export const determineBasketFulfillment = (address?: DeliveryAddress): CONVEYANCE_TYPES => {
  const sessionConveyanceType = getPersistentParameterValue(ALLOWED_URL_PARAMETER_KEYS.HANDOFF);

  if (
    sessionConveyanceType &&
    LOCKED_FLOW_CONVEYANCE_TYPES.includes(sessionConveyanceType as API_CONVEYANCE_TYPES)
  ) {
    // @ts-expect-error sessionConveyanceType can't index API_FOR_CONVEYANCE_TYPES_SWAP.
    return API_FOR_CONVEYANCE_TYPES_SWAP[sessionConveyanceType];
  }

  return address?.street_address ? CONVEYANCE_TYPES.DELIVERY : CONVEYANCE_TYPES.PICKUP;
};

/**
 * Derive a comma-separated string of a list of basket items with basket item quantities
 *
 * @param basketItems BasketItem[]
 * @returns string (name and quantity of basket item)
 */
export const deriveBasketItemsList = (basketItems: BasketItem[]): string => {
  const formattedBasketItems = basketItems.map((basketItem) => {
    const name = basketItem.product.name;
    const quantity = basketItem.quantity > 1 ? ` x${basketItem.quantity}` : '';

    return `${name}${quantity}`;
  });

  return formattedBasketItems.join(', ');
};

/**
 * Derive a comma-separated string of a list of basket item modifiers
 *
 * @param basketItem BasketItem
 * @returns string (list of comma-separated basket items)
 */
export const deriveBasketModifiersList = (basketItem: BasketItem): string => {
  const modifiers: string[] = [];
  basketItem?.options.map((option) => option?.name && modifiers.push(option.name));
  return modifiers.join(', ');
};

export const getBasketItemsCountWithQuantity = (basketItems: BasketItem[]): number => {
  let count = 0;

  basketItems.forEach((item) => {
    count += item.quantity;
  });

  return count;
};

export const areAllBasketItemsUnavailable = (
  basketItems: BasketItem[],
  unavailableItemIds: string[],
): boolean => basketItems.length > 0 && unavailableItemIds.length >= basketItems.length;

/**
 * A local basket and a remote basket item are almost identical except a local item has no id.
 * This makes checking equality between a remote basket item and a local basket item very difficult.
 * So as a work-around we can compare the following
 * - the product ids match
 * - the option ids match (these are product options like toppings, exclusings, etc)
 * - the recipeint names match (defaults to an empty string)
 * - the special instructions match (defaults to an emptry string)
 *
 * Essentially, we are cross referencing every unique value possible since we don't have per basket unique ids
 *
 * THIS IS NOT A FULLY ACCURATE COMPARISON!
 * ONLY USE WHEN COMPARING LOCAL ITEMS WITH REMOTE ITEMS!
 */
export function checkEqualityBetweenLocalAndRemoteBasketItems(
  localItem: Omit<BasketItem, 'id'>,
  remoteItem: BasketItem,
) {
  return (
    localItem.product.id === remoteItem.product.id &&
    localItem.recipient === remoteItem.recipient &&
    localItem.special_instructions === remoteItem.special_instructions &&
    localItem.options
      .map((o) => o.option_id ?? o.id)
      .sort()
      .join() ===
      remoteItem.options
        .map((o) => o.option_id)
        .sort()
        .join()
  );
}
